import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PlusCircle, Receipt, Tablet, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import {
  locationTipsSettingsGet,
  locationTipsSettingsUpdate,
} from "~/api-refs";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { ProductEvents } from "~/lib/posthog";

import { RadioPanels } from "../shared/radio-panels";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";

type TipMethod = "disabled" | "in-pos" | "on-receipt";

interface SuggestedAmount {
  type: "percentage" | "fixed-amount";
  value: number;
}

type SuggestedAmounts = SuggestedAmount[];

interface LocationTipsSettings {
  tipMethod: TipMethod;
  suggestedAmounts: SuggestedAmounts | null;
}

interface TipSuggestion {
  type: "percentage" | "fixed-amount";
  value: number;
}

interface LocationTipsSettingsFormProps {
  locationId: string;
}

export function LocationTipsSettingsForm({
  locationId,
}: LocationTipsSettingsFormProps) {
  const [openTipSuggenstionModal, setOpenTipSuggestionModal] = useState<
    number | "new" | null
  >(null);

  const { data: tipsSettingsQuery } = useQuery(
    convexQuery(locationTipsSettingsGet, {
      locationId: toConvexTableId("locations", locationId),
    })
  );

  const updateTipsSettingsMutation = useMutation({
    mutationFn: (data: {
      locationId: Id<"locations">;
      value: {
        tipMethod: TipMethod;
        suggestedAmounts?: SuggestedAmounts;
      };
    }) => convexClient.mutation(locationTipsSettingsUpdate, data),
    onSuccess: () => {
      ProductEvents.settingsUpdated("tips");
    },
  });

  const form = useForm<LocationTipsSettings>({
    values: tipsSettingsQuery
      ? {
          tipMethod: tipsSettingsQuery.tipMethod,
          suggestedAmounts: tipsSettingsQuery.suggestedAmounts ?? null,
        }
      : {
          tipMethod: "disabled",
          suggestedAmounts: [],
        },
  });
  const getSuggestedAmounts = () => form.getValues("suggestedAmounts") ?? [];

  const update = () => {
    const formValues = form.getValues();
    updateTipsSettingsMutation.mutate({
      locationId: toConvexTableId("locations", locationId),
      value: {
        tipMethod: formValues.tipMethod,
        suggestedAmounts: formValues.suggestedAmounts ?? undefined,
      },
    });
  };

  return (
    <>
      {openTipSuggenstionModal !== null && "suggestedAmounts" && (
        <TipSuggestionModal
          onClose={() => setOpenTipSuggestionModal(null)}
          onDelete={() => {
            setOpenTipSuggestionModal(null);
            if (typeof openTipSuggenstionModal !== "number") {
              return;
            }

            const currentSuggestedAmounts =
              form.getValues("suggestedAmounts") ?? [];
            const updatedSuggestedAmounts = currentSuggestedAmounts.filter(
              (_, i) => i !== openTipSuggenstionModal
            );

            form.setValue("suggestedAmounts", updatedSuggestedAmounts);
            update();
          }}
          onSubmit={(data) => {
            setOpenTipSuggestionModal(null);
            const updatedSuggestedAmounts =
              openTipSuggenstionModal === "new"
                ? [...(form.getValues("suggestedAmounts") ?? []), data]
                : getSuggestedAmounts().map((suggestion, i) =>
                    i === openTipSuggenstionModal ? data : suggestion
                  );

            form.setValue("suggestedAmounts", updatedSuggestedAmounts);
            update();
          }}
          suggestion={
            openTipSuggenstionModal === "new"
              ? undefined
              : getSuggestedAmounts()[openTipSuggenstionModal]
          }
          tipNumber={
            typeof openTipSuggenstionModal === "number"
              ? openTipSuggenstionModal
              : getSuggestedAmounts().length
          }
        />
      )}
      <Form {...form}>
        <form className="space-y-6">
          <section id="tips">
            <div className="space-y-1 py-4">
              <h2 className="subtitle-sm text-foreground">Tips</h2>
              <p className="body-sm text-muted-foreground">
                Configure tips settings for this location
              </p>
            </div>
            <div className="space-y-3">
              <FormField
                control={form.control}
                name="tipMethod"
                render={({ field }) => (
                  <>
                    <FormItem>
                      <FormLabel className="body-sm text-foreground flex flex-row items-center gap-2">
                        <FormControl>
                          <Switch
                            checked={field.value !== "disabled"}
                            onCheckedChange={(v) => {
                              field.onChange(v ? "in-pos" : "disabled");
                              update();
                            }}
                          />
                        </FormControl>
                        <div className="subtitle-sm">
                          Enable Tipping
                          <p className="body-sm text-muted-foreground">
                            Accept tips from customers on orders
                          </p>
                        </div>
                      </FormLabel>
                      <FormMessage />
                    </FormItem>
                    {field.value !== "disabled" && (
                      <FormItem className="w-2xl pl-13">
                        <FormLabel>Select tip method:</FormLabel>
                        <FormControl>
                          <RadioPanels
                            items={[
                              {
                                value: "in-pos",
                                label: "Tip on POS screen",
                                description:
                                  "Customers enter tip amounts on POS screen",
                                icon: Tablet,
                              },
                              {
                                value: "on-receipt",
                                label: "Tip on Paper Receipt",
                                description:
                                  "Customers sign and tip on printed receipt",
                                icon: Receipt,
                              },
                            ]}
                            onValueChange={(value) => {
                              field.onChange(value);
                              update();
                            }}
                            value={field.value ?? "on-pos"}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  </>
                )}
              />
            </div>
          </section>
          <section id="suggested-amounts">
            <div className="space-y-1 py-4">
              <h2 className="subtitle-sm text-foreground">
                Suggested Tip Amounts
              </h2>
              <p className="body-sm text-foreground">
                Add up to 3 preset tip amounts on POS and Paper Receipts
              </p>
            </div>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="suggestedAmounts"
                render={({ field }) => (
                  <>
                    <FormItem>
                      <FormLabel className="body-sm text-foreground flex flex-row items-center gap-2">
                        <FormControl>
                          <Switch
                            checked={field.value !== null}
                            onCheckedChange={(v) => {
                              field.onChange(v ? [] : null);
                              update();
                            }}
                          />
                        </FormControl>
                        <div className="subtitle-sm">
                          Enable Suggested Tips
                          <p className="body-sm text-muted-foreground">
                            Suggest up to 3 tip amounts to customers on receipts
                            or POS screen
                          </p>
                        </div>
                      </FormLabel>
                      <FormMessage />
                    </FormItem>
                    {field.value !== null && (
                      <div className="flex flex-wrap gap-4 pl-13">
                        {field.value?.map((suggestion, i) => (
                          <button
                            className="bg-muted flex h-36 w-48 flex-col justify-center gap-4 rounded-xl p-4"
                            key={`tip-suggestion-${suggestion.type}-${suggestion.value.toString()}`}
                            onClick={() => setOpenTipSuggestionModal(i)}
                            type="button"
                          >
                            <p className="h1 text-foreground">
                              {suggestion.type === "percentage"
                                ? `${suggestion.value}%`
                                : `$${suggestion.value}`}
                            </p>
                            <p className="text-muted-foreground">
                              Suggested Tip {i + 1}
                            </p>
                          </button>
                        ))}
                        {(field.value?.length ?? 0) < 3 && (
                          <button
                            className="border-border flex h-36 w-48 flex-col items-center justify-center gap-2 rounded-xl border p-4"
                            onClick={() => setOpenTipSuggestionModal("new")}
                            type="button"
                          >
                            <PlusCircle className="text-foreground size-6" />
                            <p className="text-foreground">
                              Add Tip Suggestion
                            </p>
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              />
            </div>
          </section>
        </form>
      </Form>
    </>
  );
}

const TipSuggestionModal = ({
  onClose,
  suggestion,
  tipNumber,
  onSubmit,
  onDelete,
}: {
  onClose: () => void;
  suggestion?: SuggestedAmounts[number];
  tipNumber: number;
  onSubmit: (data: TipSuggestion) => void;
  onDelete: () => void;
}) => {
  const form = useForm<TipSuggestion>({
    values: suggestion
      ? {
          type: suggestion.type,
          value: suggestion.value,
        }
      : {
          type: "percentage",
          value: 0,
        },
  });

  const suggestionTypeRef = useRef<"percentage" | "fixed-amount">("percentage");

  useEffect(() => {
    suggestionTypeRef.current = form.watch("type");
  }, [form.watch, form]);

  return (
    <Dialog onOpenChange={onClose} open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="h4 text-foreground text-center">
            {suggestion ? "Edit" : "Add"} Suggested Tip {tipNumber + 1}
          </DialogTitle>
          <DialogDescription className="text-foreground text-center">
            Show a suggested tip percentage or amount to customers
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <FormControl>
                    <RadioPanels
                      items={[
                        {
                          value: "percentage",
                          label: "Percentage (%)",
                          description: "Calculated percentage",
                        },
                        {
                          value: "fixed-amount",
                          label: "Amount ($)",
                          description: "Flat amount",
                        },
                      ]}
                      onValueChange={field.onChange}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount</FormLabel>
                  <FormControl>
                    <Input
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      value={field.value ?? ""}
                      {...(suggestionTypeRef.current === "percentage"
                        ? {
                            rightContent: "%",
                          }
                        : {
                            leftContent: "$",
                          })}
                      placeholder="0"
                      type="number"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex w-full items-center gap-3">
              {suggestion !== undefined && (
                <Button
                  className="gap-1.5"
                  onClick={onDelete}
                  size="lg"
                  type="button"
                  variant="secondary"
                >
                  <Trash2 className="size-4" />
                  Delete
                </Button>
              )}
              <Button className="w-full" size="lg">
                Save
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
